(function () {
  const dataEl = document.getElementById("gtm-page-data");
  if (!dataEl) return;

  /** @type {any} */
  let pageData;
  try {
    pageData = JSON.parse(dataEl.textContent || "");
  } catch {
    return;
  }

  const { pageType, currency } = pageData;

  // Cast window once to avoid repeated type suppressions
  const win = /** @type {any} */ (window);
  win.dataLayer = win.dataLayer || [];

  // Clear previous ecommerce object before each push, per GA4 spec
  /** @param {Record<string, unknown>} obj */
  function push(obj) {
    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push(obj);
  }

  // Build items array from product card data attributes (collection + search pages)
  /**
   * @param {string} listName
   * @param {string} listId
   * @returns {any[]}
   */
  function getCardItems(listName, listId) {
    /** @type {any[]} */
    const items = [];
    document
      .querySelectorAll("product-card[data-gtm-item]")
      .forEach((card, index) => {
        try {
          const el = /** @type {HTMLElement} */ (card);
          const item = JSON.parse(el.dataset.gtmItem || "");
          items.push({
            ...item,
            index: index + 1,
            item_list_name: listName,
            item_list_id: listId,
          });
        } catch {}
      });
    return items;
  }

  // view_item
  if (pageType === "product" && pageData.product) {
    push({
      event: "view_item",
      ecommerce: {
        currency,
        value: pageData.product.price,
        items: [pageData.product],
      },
    });
  }

  // view_cart
  if (pageType === "cart" && pageData.items) {
    push({
      event: "view_cart",
      ecommerce: { currency, value: pageData.value, items: pageData.items },
    });
  }

  // view_item_list (collection + search)
  if (pageType === "collection" || pageType === "search") {
    const listName = pageData.listName || "";
    const listId = String(pageData.listId || "");
    const items = getCardItems(listName, listId);
    if (items.length) {
      push({
        event: "view_item_list",
        ecommerce: {
          currency,
          item_list_name: listName,
          item_list_id: listId,
          items,
        },
      });
    }
  }

  // search
  if (pageType === "search" && pageData.searchQuery) {
    push({ event: "search", search_term: pageData.searchQuery });
  }

  // select_item — click on a product card link
  document.addEventListener("click", function (e) {
    const target = /** @type {Element | null} */ (e.target);
    if (!target) return;
    const card = /** @type {HTMLElement | null} */ (
      target.closest("product-card[data-gtm-item]")
    );
    if (!card) return;
    if (!target.closest("a")) return;

    let item;
    try {
      item = JSON.parse(card.dataset.gtmItem || "");
    } catch {
      return;
    }

    const listName = pageData.listName || "";
    const listId = String(pageData.listId || "");
    push({
      event: "select_item",
      ecommerce: {
        currency,
        item_list_name: listName,
        item_list_id: listId,
        items: [item],
      },
    });
  });

  // add_to_cart: addedItems (IRL) → resource.items (quick-add) → variantMap / gtmItemMap
  const variantMap = /** @type {Record<string, any>} */ (
    pageData.variants || {}
  );

  /** @type {Record<string, any>} */
  const productCardMap = {};
  document
    .querySelectorAll("product-card[data-product-id][data-gtm-item]")
    .forEach((card) => {
      try {
        const el = /** @type {HTMLElement} */ (card);
        productCardMap[el.dataset.productId || ""] = JSON.parse(
          el.dataset.gtmItem || "",
        );
      } catch {}
    });

  /** @returns {Record<string, any>} */
  function gtmItemMap() {
    /** @type {Record<string, any>} */
    const map = { ...productCardMap };
    document
      .querySelectorAll("[data-gtm-item][data-product-id]")
      .forEach((el) => {
        if (el.closest("product-card")) return;
        try {
          const node = /** @type {HTMLElement} */ (el);
          map[node.dataset.productId || ""] = JSON.parse(
            node.dataset.gtmItem || "",
          );
        } catch {}
      });
    return map;
  }

  // Build a GA4 item from a Shopify AJAX cart line item (has vendor + product_type).
  /** @param {any} line */
  function itemFromCartLine(line) {
    return {
      item_id: String(line.variant_id ?? ""),
      item_name: line.product_title ?? line.title ?? "",
      item_brand: line.vendor ?? "",
      item_category: line.product_type ?? "",
      item_variant: line.variant_title ?? "",
      price: (line.final_price ?? line.price ?? 0) / 100,
      quantity: line.quantity ?? 1,
      gender: "Women",
    };
  }

  document.addEventListener("cart:update", function (e) {
    const ce = /** @type {CustomEvent} */ (e);
    if (!ce.detail?.data) return;
    const { source, didError, itemCount, productId } = ce.detail.data;
    if (
      (source !== "product-form-component" && source !== "irl-quick-add") ||
      didError
    )
      return;

    const addedItems = ce.detail.data.addedItems;
    if (Array.isArray(addedItems) && addedItems.length) {
      const stamped = gtmItemMap()[productId] || {};
      const items = addedItems.map((line) => ({
        ...stamped,
        ...itemFromCartLine(line),
      }));
      const value = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
      push({ event: "add_to_cart", ecommerce: { currency, value, items } });
      return;
    }

    // Quick-add paths dispatch the added line in resource; IRL sends the full cart
    // in resource instead — never map all cart lines for irl-quick-add.
    const resource = ce.detail.resource;
    if (
      source === "product-form-component" &&
      resource &&
      Array.isArray(resource.items) &&
      resource.items.length
    ) {
      const items = resource.items.map(itemFromCartLine);
      const value = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
      push({ event: "add_to_cart", ecommerce: { currency, value, items } });
      return;
    }

    const variantId = /** @type {string} */ (String(ce.detail.sourceId || ""));
    const quantity = /** @type {number} */ (
      source === "irl-quick-add" ? 1 : itemCount || 1
    );
    const formVariantId =
      ce.target instanceof Element
        ? ce.target.querySelector('input[name="id"]')?.value || ""
        : "";
    const fromVariantMap = variantId in variantMap;
    const item =
      (fromVariantMap ? variantMap[variantId] : null) ||
      (formVariantId in variantMap ? variantMap[formVariantId] : null) ||
      gtmItemMap()[productId];
    if (!item) return;

    push({
      event: "add_to_cart",
      ecommerce: {
        currency,
        value: /** @type {number} */ (item.price) * quantity,
        items: [
          {
            ...item,
            ...(fromVariantMap || /^\d+$/.test(variantId)
              ? { item_id: variantId || formVariantId }
              : {}),
            quantity,
          },
        ],
      },
    });
  });

  // sign_up — Klaviyo forms dispatch a 'klaviyoForms' event on successful submission
  document.addEventListener("klaviyoForms", function (e) {
    const ce = /** @type {CustomEvent} */ (e);
    if (ce.detail?.type !== "submit") return;
    push({ event: "sign_up", method: "email" });
  });
})();
